import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Pencil, Trash2, Star, RefreshCw, Plus, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { cmsApi, apiError, mediaUrl } from "../api";
import { usePageData, PageTitle, Card, TextField, SelectField, Toggle, ConfirmDialog, OrderButtons, moveItem, Spinner } from "../components/admin-ui";

function EditDialog({ img, categories, onClose, onSaved }) {
  const [f, setF] = useState({ title: img.title || "", description: img.description || "", category_id: img.category_id || null, featured: !!img.featured });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await cmsApi.put(`/website-admin/gallery/${img.id}`, f); toast.success("Bild aktualisiert"); onSaved(); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="gallery-edit-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Bild bearbeiten</DialogTitle><DialogDescription>Titel, Beschreibung und Kategorie.</DialogDescription></DialogHeader>
        <img src={mediaUrl(img.media_id)} alt="" className="w-full h-40 object-cover rounded-lg" />
        <div className="space-y-3">
          <TextField label="Titel" value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="gallery-title-input" />
          <TextField label="Beschreibung" value={f.description} onChange={(v) => setF({ ...f, description: v })} textarea rows={2} testid="gallery-description-input" />
          <SelectField label="Kategorie" value={f.category_id} onChange={(v) => setF({ ...f, category_id: v })} options={categories.map((c) => ({ value: c.id, label: c.name }))} testid="gallery-category-select" />
          <Toggle label="Hervorgehoben (Startseite bevorzugt)" checked={f.featured} onChange={(v) => setF({ ...f, featured: v })} testid="gallery-featured-toggle" />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Abbrechen</Button><Button onClick={save} disabled={saving} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white" data-testid="gallery-edit-save">Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function GalleryAdmin() {
  const { data, loading, reload } = usePageData("/website-admin/gallery");
  const [filter, setFilter] = useState("all");
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [uploading, setUploading] = useState(0);
  const uploadRef = useRef(null);
  const replaceRef = useRef(null);
  const [replaceId, setReplaceId] = useState(null);
  if (loading || !data) return <Spinner />;

  const cats = data.categories;
  const images = data.images.filter((i) => filter === "all" || i.category_id === filter);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(files.length);
    let ok = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      if (filter !== "all") fd.append("category_id", filter);
      try { await cmsApi.post("/website-admin/gallery/upload", fd); ok++; } catch (err) { toast.error(`${file.name}: ${apiError(err)}`); }
      setUploading((n) => n - 1);
    }
    if (ok) toast.success(`${ok} Bild${ok > 1 ? "er" : ""} hochgeladen`);
    reload();
  };
  const replace = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !replaceId) return;
    const fd = new FormData(); fd.append("file", file);
    try { await cmsApi.put(`/website-admin/gallery/${replaceId}/image`, fd); toast.success("Bild ersetzt"); reload(); } catch (err) { toast.error(apiError(err)); }
    setReplaceId(null);
  };
  const reorder = async (i, dir) => {
    const moved = moveItem(images, i, dir);
    const rest = data.images.filter((x) => !images.includes(x));
    try { await cmsApi.put("/website-admin/gallery/reorder", { ids: [...moved, ...rest].map((x) => x.id) }); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const addCat = async () => {
    if (!newCat.trim()) return;
    try { await cmsApi.post("/website-admin/gallery-categories", { name: newCat.trim() }); setNewCat(""); toast.success("Kategorie angelegt"); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const confirmDelete = async () => {
    try {
      await cmsApi.delete(del.type === "cat" ? `/website-admin/gallery-categories/${del.id}` : `/website-admin/gallery/${del.id}`);
      toast.success("Gelöscht"); if (del.type === "cat" && filter === del.id) setFilter("all"); setDel(null); reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="gallery-admin-page">
      <PageTitle title="Galerie" text="Bilder hochladen, ordnen und kategorisieren. Bilder werden automatisch für das Web optimiert."
        action={<Button onClick={() => uploadRef.current?.click()} disabled={uploading > 0} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white" data-testid="gallery-upload-button">{uploading > 0 ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{uploading > 0 ? `${uploading} wird hochgeladen…` : "Bilder hochladen"}</Button>} />
      <input ref={uploadRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={upload} data-testid="gallery-upload-input" />
      <input ref={replaceRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={replace} data-testid="gallery-replace-input" />

      <Card className="mb-6" testid="gallery-categories">
        <div className="flex flex-wrap items-center gap-2">
          {[{ id: "all", name: "Alle Bilder" }, ...cats].map((c) => (
            <span key={c.id} className={`inline-flex items-center rounded-full border text-sm ${filter === c.id ? "bg-[#4A1D24] text-white border-[#4A1D24]" : "bg-white text-stone-700 border-stone-200"}`}>
              <button onClick={() => setFilter(c.id)} className="px-3 py-1.5" data-testid={`gallery-cat-${c.id}`}>{c.name}</button>
              {c.id !== "all" && <button onClick={() => setDel({ type: "cat", id: c.id, name: c.name })} className="pr-2 opacity-60 hover:opacity-100" aria-label="Kategorie löschen" data-testid={`gallery-cat-delete-${c.id}`}><X className="h-3.5 w-3.5" /></button>}
            </span>
          ))}
          <div className="flex items-center gap-1 ml-auto">
            <input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} placeholder="Neue Kategorie" className="h-9 rounded-md border border-input bg-[#FAF7F2]/60 px-3 text-sm" data-testid="gallery-new-cat-input" />
            <Button variant="outline" size="sm" onClick={addCat} data-testid="gallery-new-cat-button"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        {filter !== "all" && <p className="text-xs text-stone-500 mt-3">Neue Uploads werden der Kategorie „{cats.find((c) => c.id === filter)?.name}“ zugeordnet.</p>}
      </Card>

      {images.length === 0 && <Card><p className="text-sm text-stone-500">Noch keine Bilder. Laden Sie Ihre ersten Fotos hoch.</p></Card>}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="gallery-admin-grid">
        {images.map((img, i) => (
          <div key={img.id} className="rounded-2xl bg-white border border-stone-200/80 shadow-sm overflow-hidden" data-testid={`gallery-admin-item-${img.id}`}>
            <div className="relative aspect-[4/3] bg-[#F1EAE0]">
              <img src={mediaUrl(img.media_id)} alt={img.title} className="absolute inset-0 w-full h-full object-cover" />
              {img.featured && <Star className="absolute top-2 left-2 h-4 w-4 text-[#C59B6C] fill-[#C59B6C] drop-shadow" />}
            </div>
            <div className="p-3">
              <p className="text-sm font-medium text-stone-900 truncate">{img.title || <span className="text-stone-400 italic">Ohne Titel</span>}</p>
              <p className="text-[11px] text-stone-500 truncate">{cats.find((c) => c.id === img.category_id)?.name || "Ohne Kategorie"}</p>
              <div className="flex items-center gap-1 mt-2">
                <OrderButtons testid={`gallery-${img.id}`} disableUp={i === 0} disableDown={i === images.length - 1} onUp={() => reorder(i, -1)} onDown={() => reorder(i, 1)} />
                <span className="flex-1" />
                <button onClick={() => { setReplaceId(img.id); replaceRef.current?.click(); }} className="p-1.5 text-stone-400 hover:text-[#4A1D24]" aria-label="Bild ersetzen" data-testid={`gallery-replace-${img.id}`}><RefreshCw className="h-4 w-4" /></button>
                <button onClick={() => setEdit(img)} className="p-1.5 text-stone-400 hover:text-[#4A1D24]" aria-label="Bearbeiten" data-testid={`gallery-edit-${img.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setDel({ type: "img", id: img.id, name: img.title || "Bild" })} className="p-1.5 text-stone-400 hover:text-red-700" aria-label="Löschen" data-testid={`gallery-delete-${img.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {edit && <EditDialog img={edit} categories={cats} onClose={() => setEdit(null)} onSaved={reload} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title={del?.type === "cat" ? "Kategorie löschen?" : "Bild löschen?"} text={del?.type === "cat" ? `Kategorie „${del?.name}“ wird entfernt; die Bilder bleiben erhalten.` : `„${del?.name}“ wird dauerhaft von der Website entfernt.`} onConfirm={confirmDelete} testid="delete-dialog" />
    </div>
  );
}
